import React from 'react';

enum TextVariant {
  LEAD = 'lead',
  BODY_LARGE = 'body-large',
  BODY = 'body',
  BODY_SMALL = 'body-small',
  CAPTION = 'caption',
}

enum TextWeight {
  LIGHT = 'light',
  NORMAL = 'normal',
  MEDIUM = 'medium',
  SEMIBOLD = 'semibold',
  BOLD = 'bold',
}

enum TextColor {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  MUTED = 'muted',
  WHITE = 'white',
  INHERIT = 'inherit',
}

enum TextAlign {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
  JUSTIFY = 'justify',
}

enum MarginBottom {
  NONE = 'none',
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
}

enum HtmlTag {
  P = 'p',
  SPAN = 'span',
  DIV = 'div',
}

interface TextProps {
  children: React.ReactNode;
  variant?: TextVariant;
  weight?: TextWeight;
  color?: TextColor;
  align?: TextAlign;
  className?: string;
  marginBottom?: MarginBottom;
  as?: HtmlTag;
}

const variantClasses = {
  lead: 'text-lg md:text-xl leading-relaxed',
  'body-large': 'text-lg leading-relaxed',
  body: 'text-base leading-relaxed',
  'body-small': 'text-sm leading-relaxed',
  caption: 'text-xs leading-normal',
};

const weightClasses = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const colorClasses = {
  primary: 'text-gray-900',
  secondary: 'text-gray-700',
  muted: 'text-gray-600',
  white: 'text-white',
  inherit: 'text-inherit',
};

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

const marginBottomClasses = {
  none: 'mb-0',
  xs: 'mb-1',
  sm: 'mb-2',
  md: 'mb-4',
  lg: 'mb-6',
  xl: 'mb-8',
};

export {TextVariant, TextWeight, TextColor, TextAlign, MarginBottom, HtmlTag};

export const Text: React.FC<TextProps> = ({
  children,
  variant = TextVariant.BODY,
  weight = TextWeight.NORMAL,
  color = TextColor.SECONDARY,
  align = TextAlign.LEFT,
  className = '',
  marginBottom = MarginBottom.NONE,
  as = HtmlTag.P,
}) => {
  const Tag = as;

  const classes = [
    variantClasses[variant],
    weightClasses[weight],
    colorClasses[color],
    alignClasses[align],
    marginBottomClasses[marginBottom],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <Tag className={classes}>{children}</Tag>;
};
